import type { GetServerSidePropsContext } from 'next';
import { useConfig } from 'shared/config/ConfigContext';
import { Head } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import { SWRConfig } from 'swr';
import { getCurrentCancellationPolicy } from '@/api/apiInternal';
import { fetchObject } from '@/api/objectApi';
import { userLookup } from '@/api/userApi';
import type { ObjectData } from '@/domain';
import type { User } from '@/domain/userLookup';
import { DATA_KEY as CURRENT_CANCELLATION_POLICY_KEY } from '@/hooks/useCurrentCancellationPolicy';
import { USE_OBJECT_KEY } from '@/hooks/useObject';
import { CreateRentalRequestPage } from '@/scenes/createRentalRequestPage/CreateRentalRequestPage';
import { clientConfig } from '@/server/config';
import { buildCommonProps, sanitizeCommonProps } from '@/server/data';
import { wrapper } from '@/store/store';

type Props = {
    user: User | null;
    swrFallback: { [USE_OBJECT_KEY]: ObjectData };
};

const CreateRentalRequest = (props: Props) => {
    const { baseUrl } = useConfig();
    wrapper.useHydration(props);
    const objectData = props.swrFallback[USE_OBJECT_KEY];

    return (
        <SWRConfig value={{ fallback: props.swrFallback }}>
            <Head
                title="Feriehjem og hytter - Send forespørsel | FINN reise"
                description="Send forespørsel til utleier."
                canonical={`${baseUrl}/reise/feriehus-hytteutleie/opprettforesporsel/?finnkode=${objectData.adId}`}
            />
            <Main broadcastUrl="/broadcasts?path=www.finn.no/reise/feriehus-hytteutleie">
                <CreateRentalRequestPage user={props.user} />
            </Main>
        </SWRConfig>
    );
};

export const getServerSideProps = wrapper.getServerSideProps(() => async ({ req, res, query }: GetServerSidePropsContext) => {
    const commonProps = await buildCommonProps(req, res, query);
    const itemId = typeof query?.finnkode === 'string' ? query.finnkode : '';

    const objectData = await fetchObject(itemId, commonProps.authCookies, req.cookies.USERID)
        .then((data) => data)
        .catch(() => null);

    let user: User | null;
    if (!commonProps.loginId) {
        user = null;
    } else {
        user = await userLookup(commonProps.loginId?.toString(), commonProps.loginJwt);
    }

    if (!objectData) {
        return { notFound: true };
    }

    const currentCancellationPolicy = await getCurrentCancellationPolicy(objectData.adId);

    const swrFallback = {
        [USE_OBJECT_KEY]: objectData,
        [CURRENT_CANCELLATION_POLICY_KEY]: currentCancellationPolicy,
    };

    return {
        props: {
            config: clientConfig,
            ...sanitizeCommonProps(commonProps),
            user,
            swrFallback,
        },
    };
});

export default CreateRentalRequest;
